import React, { useState, useEffect, useContext } from "react";
import ActionsContext from "common/ActionsContext";
import styled from "styled-components";
import Request from "common/Request";
import Modal from "common/Modal";
import BqnContext from "common/BqnContext";
import { getIndexes } from "common/utils";
import { getBqnIdCmdPart } from "common/api";
import Table from "common/graphs/Table/Table";

const doesNothing = () => {};

const TableContainer = styled.div`
  width: 100%;
  overflow: auto;
`;

const getColumns = ({ mode, send, setDeleteSelected, setShowDeleteModal }) => {
  return [
    ...(mode !== "standalone"
      ? [
          {
            label: "BQN",
            id: "BQN",
            numeric: true,
            title: "BQN ID",
          },
        ]
      : []),
    {
      label: "USERNAME",
      id: "USER",
      numeric: false,
      title: "USER",
    },
    {
      label: "PROFILE",
      id: "PROFILE",
      numeric: false,
      title: "PROFILE",
    },
    {
      label: "DESCRIPTION",
      id: "NAME",
      numeric: false,
      title: "DESCRIPTION",
    },
    {
      label: "ACTIONS",
      id: "ACTIONS",
      actions: [
        {
          label: "Change Password",
          icon: "lock",
          onClick: (row) =>
            send("change-password", {
              user: row["USER"],
              bqnId: row["BQN"] ? row["BQN"] : null,
            }),
        },
        {
          label: "Delete",
          icon: "delete",
          onClick: (row) => {
            setDeleteSelected([
              { user: row["USER"], bqnId: row["BQN"] !== undefined ? row["BQN"] : null },
            ]);
            setShowDeleteModal(true);
          },
        },
      ],
    },
  ];
};

const avoidWarningByCrontab = (response) =>
  response.match(/no contrab for /) !== null;

const existsAndNotAvoidable = (response, ...exceptions) =>
  response &&
  response.length > 0 &&
  exceptions.find((exceptional) => exceptional(response)) !== undefined;

const reportIfFailsDuring = (action, process) =>
  process
    .then((response) => {
      if (existsAndNotAvoidable(response, avoidWarningByCrontab)) {
        console.log("user.<%s>.error: <%s>", action, response);
        throw `Warning: ${response}`;
      }
    })
    .catch(window.showModalError);

const DeleteModalWrapper = styled.div`
  ul {
    padding: 0 0 0 15px;
    margin: -20px 0 20px 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 20px;

    li {
      margin: 3px 0 !important;
    }
  }
`;

const DeleteModal = ({
  showDeleteModal,
  setShowDeleteModal,
  items = [],
  whenDone,
}) => {
  const handleApply = () => {
    const cmds = items.map(
      ({ user, bqnId }) =>
        `system users delete ${user} force${bqnId ? ` bqn-id ${bqnId}` : ""}`,
    );
    reportIfFailsDuring(
      "delete",
      ifCl.run(`${cmds.join("\n")}\n`, /*is batch*/ true),
    ).then(() => {
      setShowDeleteModal(false);
      whenDone();
    });
  };
  if (!showDeleteModal) {
    return null;
  }
  const usernames = items.map((item) => item.user);
  const title =
    usernames.length === 1
      ? `Delete "${usernames[0]}" user?`
      : `Delete ${usernames.length} users?`;
  return (
    <Modal
      title={title}
      onClose={() => setShowDeleteModal(false)}
      closeLabel="CANCEL"
      superIcon={"delete_forever"}
      large={usernames.length > 1 && usernames.length < 100}
      content={() => (
        <DeleteModalWrapper>
          {usernames.length === 1 ? (
            <p>
              <b>WARNING</b>: The user will be permanently deleted together with
              all the files inside its home directory.
            </p>
          ) : null}
          {usernames.length > 1 ? (
            <>
              {usernames.length < 100 ? (
                <ul>
                  {usernames.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
              ) : null}
              <p>
                <b>WARNING</b>: The users will be permanently deleted together
                with all the files inside their home directories.
              </p>
            </>
          ) : null}
        </DeleteModalWrapper>
      )}
      applyLabel="YES"
      onApply={handleApply}
    />
  );
};

const UsersList = ({ response, send = doesNothing }) => {
  const {
    clusterStatus: { mode },
  } = useContext(BqnContext);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteSelected, setDeleteSelected] = useState([]);
  const [selected, setSelected] = useState([]);

  const [header, ...rows] = response.trim().split("\n");
  const headerIndex = getIndexes(header);
  const headerAttrs = header.trim().split(/\s+/);
  const newRows = rows.map((row) => {
    const rowAttrs = row.trim(/\s/).split(/\s+/);
    const newEntry = rowAttrs.reduce((acc, attr, index) => {
      if (!headerAttrs[index]) {
        acc[headerAttrs[index - 1]] = `${acc[headerAttrs[index - 1]]} ${attr}`;
      } else {
        acc[headerAttrs[index]] = attr;
      }
      return acc;
    }, {});
    return newEntry;
  });

  const filteredRows = newRows
    .filter((row) => row["USER"] !== "root")
    .map((row, index) => ({
      ...row,
      id: row.id !== undefined ? row.id : index,
    }));

  const columns = getColumns({
    mode,
    send,
    setDeleteSelected,
    setShowDeleteModal,
  });

  return (
    <>
      <TableContainer>
        <Table
          isSelectable={true}
          isRowSelectable={false}
          data={filteredRows}
          sort={true}
          columns={columns}
          search={true}
          csvPrefix="users"
          rowsPerPageNr={50}
          selected={selected}
          setSelected={setSelected}
          deleteSelected={(values) => {
            const selectedItems = filteredRows
              .filter((row) => values.includes(row.id))
              .map((row) => ({
                user: row["USER"],
                bqnId: row["BQN"] ? row["BQN"] : null,
              }));
            setDeleteSelected(selectedItems);
            setShowDeleteModal(true);
          }}
        />
      </TableContainer>
      <DeleteModal
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        items={deleteSelected}
        whenDone={() => {
          setSelected([]);
          send("do-load");
        }}
      />
    </>
  );
};

const ListUsers = () => {
  const [request, setRequest] = useState(null);
  const actions = useContext(ActionsContext);
  const {
    bqnId,
    clusterStatus: { mode },
  } = useContext(BqnContext);

  const sendSignal = (...args) => actions.send(...args);

  const doRequest = () => {
    const cmd =
      mode === "cluster" && bqnId !== "all"
        ? `show system users${getBqnIdCmdPart(mode, bqnId)}`
        : "show system users detail";
    return ifCl.run(cmd).then((response) => ({ response }));
  };

  const doLoad = () => {
    setRequest(doRequest());
  };

  useEffect(() => {
    doLoad();
    return actions.recv("do-load", doLoad);
  }, [bqnId]);

  return request === null ? null : (
    <Request during={request}>
      {(result) => (
        <>
          <UsersList {...result} send={sendSignal} />
        </>
      )}
    </Request>
  );
};

export default ListUsers;
